import { StatusPill, type PillTone } from '@siliconx/shared/ui/StatusPill';
import type { EnvironmentalBasis } from '@siliconx/shared';

/**
 * SIL-472 — the small basis chip EVERY Environmental Overview widget
 * renders, per the plan's data-layer contract ("every widget renders a
 * small basis-tag chip; sample-tagged widgets are visually honest"). Reuses
 * the shared `StatusPill` primitive rather than inventing a new chip
 * component — see DESIGN.md's Chips convention.
 *
 * 'sample' deliberately does NOT use a status-meaning tone (green/amber/
 * red) — it isn't a verdict on data quality, it's a plain statement that
 * the number is not real. Neutral (grey) keeps it visually distinct from
 * the AVOIDED tile's own sky-family quarantine treatment (a different,
 * stronger signal reserved for that one tile).
 */
const BASIS_LABEL: Record<EnvironmentalBasis, string> = {
  measured: 'Measured',
  derived: 'Derived',
  platform_default: 'Platform default',
  sample: 'Sample',
};

const BASIS_TONE: Record<EnvironmentalBasis, PillTone> = {
  measured: 'green',
  derived: 'green',
  platform_default: 'amber',
  sample: 'neutral',
};

const BASIS_TITLE: Record<EnvironmentalBasis, string> = {
  measured: 'Computed with no defaulted inputs.',
  derived: 'Computed from measured/partial inputs via a published formula.',
  platform_default: 'A published platform constant stands in for a missing input.',
  sample: 'Not real data — prod has no rows on the underlying surface yet.',
};

export function BasisTag({ basis, className }: { basis: EnvironmentalBasis | undefined; className?: string }) {
  if (!basis) return null;
  return (
    <StatusPill tone={BASIS_TONE[basis]} title={BASIS_TITLE[basis]} className={className}>
      {BASIS_LABEL[basis]}
    </StatusPill>
  );
}
